// SEO Publish Targets — operator UI for the per-brand CMS connectors
// that back the article auto-publish pipeline. Lives at
// /seo-management/publish-targets.
//
// Four adapter kinds:
//   - wordpress: WP REST API + Application Password (basic auth).
//   - ghost:     Ghost Admin API + key:secret JWT signing.
//   - webhook:   POST to a URL, optional HMAC sig.
//   - custom:    Configurable URL + method + auth + headers + body
//                template — the escape hatch for anything else.
//
// Secrets are write-only from the FE: the GET responses include
// _hasWpAppPassword / _hasGhostKey / _hasWebhookSecret / _hasCustomAuth
// booleans, never the ciphertext. The form shows "•••••• change" when
// a secret is already set; submitting an empty string preserves it.

import React, { useCallback, useEffect, useState } from 'react';
import { get, post, putGeneral, remove } from '../../api';

const KINDS = [
  { key: 'wordpress',      label: 'WordPress',       description: 'WP REST API + Application Password' },
  { key: 'ghost',          label: 'Ghost',           description: 'Ghost Admin API (key:secret)' },
  { key: 'webhook',        label: 'Generic webhook', description: 'POST JSON to a URL (HMAC optional)' },
  { key: 'custom',         label: 'Custom endpoint', description: 'Configurable URL / method / auth / body template' },
  { key: 'brand_learning', label: 'On-site learning page', description: "The brand's own /learning FAQ + articles pages" },
];

function kindLabel(kind) {
  const k = KINDS.find((x) => x.key === kind);
  return k ? k.label : kind;
}

function statusChip(target) {
  // Only show the error state if the LAST error is more recent than the last
  // successful publish — a target that published fine after an old failure has
  // recovered and shouldn't keep showing red (the "expired-looking" targets).
  const errAt = target.lastErrorAt ? new Date(target.lastErrorAt).getTime() : 0;
  const okAt = target.lastPublishedAt ? new Date(target.lastPublishedAt).getTime() : 0;
  const hasError = !!target.lastError && errAt >= okAt;
  const bg = hasError ? '#fdecec' : (target.successCount ? '#e7f5ec' : '#fafbfc');
  const fg = hasError ? '#a71d2a' : (target.successCount ? '#1e6b3e' : '#888');
  const text = hasError ? 'last error' : (target.successCount ? `${target.successCount} sent` : 'never used');
  return <span style={{ display: 'inline-block', padding: '1px 8px', borderRadius: 10, fontSize: 11, background: bg, color: fg }}>{text}</span>;
}

function Modal({ title, onClose, wide, children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 5000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: 10, padding: 20, width: wide ? 720 : 480, maxWidth: '92vw', maxHeight: '92vh', overflow: 'auto', boxShadow: '0 10px 30px rgba(0,0,0,0.25)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <h5 style={{ margin: 0 }}>{title}</h5>
          <button type="button" onClick={onClose} style={{ border: 'none', background: 'transparent', fontSize: 22, cursor: 'pointer' }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

// Form for one target. `target` may be partial (new) or the existing
// row. We initialise from target so the form survives a re-render.
function TargetForm({ target, onClose, onSaved }) {
  const isEdit = !!(target && target._id);
  const [form, setForm] = useState({ enabled: true, kind: 'wordpress', defaultStatus: 'draft', contentType: 'article', customMethod: 'POST', customAuthType: 'none', ...target });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Connected Email-Management accounts, to let a target "post as" one of them
  // (author identity). Best-effort: if the operator lacks emailAccountView or
  // has none connected, the picker is simply empty and the manual author name /
  // email fields are used instead.
  const [emailAccounts, setEmailAccounts] = useState([]);
  useEffect(() => {
    let alive = true;
    get('/api/email/accounts')
      .then((res) => { if (alive) setEmailAccounts((res && res.data) || []); })
      .catch(() => { if (alive) setEmailAccounts([]); });
    return () => { alive = false; };
  }, []);

  // Live SMTP auth test for the selected email account. Runs the moment an
  // account is picked (and after override creds are entered) so we can show
  // "authenticated ✓" or reveal a credentials-entry form on failure.
  const [authTest, setAuthTest] = useState({ status: 'idle' });
  const testEmailAuth = useCallback(async (accountId, smtpUser, smtpPassword) => {
    if (!accountId) { setAuthTest({ status: 'idle' }); return; }
    setAuthTest({ status: 'testing' });
    try {
      const res = await post('/api/seo/publish-targets/test-email-auth',
        { emailAccount_id: accountId, smtpUser: smtpUser || '', smtpPassword: smtpPassword || '' }, false);
      if (res && res.ok) setAuthTest({ status: 'ok' });
      else setAuthTest({ status: 'fail', error: (res && res.error) || 'authentication failed' });
    } catch (e) {
      setAuthTest({ status: 'fail', error: (e && (e.message || (e.response && e.response.data && e.response.data.error))) || 'authentication failed' });
    }
  }, []);
  // On open: if editing a target that already has an account linked, test it.
  useEffect(() => { if (target && target.emailAccount_id) testEmailAuth(target.emailAccount_id); }, [target, testEmailAuth]);

  const save = async () => {
    setBusy(true); setErr(null);
    try {
      // Strip the _has* booleans before submit — they're FE-only.
      const body = {};
      for (const [k, v] of Object.entries(form)) {
        if (k.startsWith('_has')) continue;
        if (k === '_id') continue;
        body[k] = v;
      }
      let res;
      if (isEdit) res = await putGeneral(`/api/seo/publish-targets/${target._id}`, body);
      else        res = await post('/api/seo/publish-targets', body, false);
      if (res && res.error) throw new Error(res.error);
      onSaved && onSaved();
    } catch (e) { setErr((e && (e.message || (e.response && e.response.data && e.response.data.error))) || 'Save failed'); }
    finally { setBusy(false); }
  };

  // Tiny label+input row so the form body stays compact.
  const Field = ({ label, children }) => (
    <label style={{ display: 'block', fontSize: 12, marginBottom: 8 }}>
      {label}
      <div style={{ marginTop: 4 }}>{children}</div>
    </label>
  );
  const text = (k, opts = {}) => (
    <input type={opts.type || 'text'} className="form-control" value={form[k] || ''} placeholder={opts.placeholder || ''}
           onChange={(e) => set(k, e.target.value)} style={{ fontSize: 13 }} />
  );
  const secretField = (k, label, hasFlag) => (
    <Field label={label + (hasFlag ? ' (••• change)' : ' *')}>
      <input type="password" autoComplete="new-password" className="form-control" placeholder={hasFlag ? 'leave blank to keep existing' : ''} value={form[k] || ''}
             onChange={(e) => set(k, e.target.value)} style={{ fontSize: 13 }} />
    </Field>
  );

  return (
    <Modal title={isEdit ? `Edit: ${target.name || ''}` : 'New publish target'} onClose={onClose}>
      {err && <div className="alert alert-warning" style={{ fontSize: 13 }}>{err}</div>}
      <Field label="Display name *">{text('name', { placeholder: 'e.g. Main blog' })}</Field>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <Field label="Kind *">
          <select className="form-control" value={form.kind || 'wordpress'} onChange={(e) => set('kind', e.target.value)} disabled={isEdit} style={{ fontSize: 13 }}>
            {KINDS.map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
          </select>
        </Field>
        <Field label="Default status">
          <select className="form-control" value={form.defaultStatus || 'draft'} onChange={(e) => set('defaultStatus', e.target.value)} style={{ fontSize: 13 }}>
            <option value="draft">Draft (review on the CMS first)</option>
            <option value="publish">Publish (live immediately)</option>
            <option value="pending">Pending review</option>
            <option value="private">Private</option>
          </select>
        </Field>
      </div>
      <Field label="Brand (optional)">{text('brand_id', { placeholder: 'leave blank for company-wide' })}</Field>

      {/* CONTENT SCOPE — what this target receives */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <Field label="Publishes *">
          <select className="form-control" value={form.contentType || 'article'} onChange={(e) => set('contentType', e.target.value)} style={{ fontSize: 13 }}>
            <option value="article">Articles / blog</option>
            <option value="faq">FAQ answers</option>
          </select>
        </Field>
        {form.contentType === 'article' && (
          <Field label="Category (optional)">{text('category', { placeholder: 'e.g. Tutorials, Blogs — blank = all' })}</Field>
        )}
      </div>
      <div style={{ fontSize: 11, color: '#888', marginTop: -2, marginBottom: 8 }}>
        {form.contentType === 'faq'
          ? 'Receives published FAQ answers for this brand.'
          : 'Receives published articles. A category restricts it to that one category; leave blank to receive every category.'}
      </div>

      {/* BRAND_LEARNING — the brand's own on-site /learning destination */}
      {form.kind === 'brand_learning' && (
        <>
          <Field label="Learning page URL *">
            {text('learningUrl', { placeholder: form.contentType === 'faq' ? 'https://app.<brand>/learning' : 'https://app.<brand>/learning/articles?category=Tutorials' })}
          </Field>
          <div style={{ fontSize: 11, color: '#888', marginTop: -4, marginBottom: 8 }}>
            Content already reaches this on-site page automatically. This entry records the canonical URL (its <code>?category=</code> sets the category) so it shows in publish history and the pipeline — it doesn't push a second copy.
          </div>
        </>
      )}

      {/* WORDPRESS */}
      {form.kind === 'wordpress' && (
        <>
          <Field label="WordPress URL *">{text('wpUrl', { placeholder: 'https://blog.example.com' })}</Field>
          <Field label="Username *">{text('wpUsername')}</Field>
          {secretField('wpAppPassword', 'Application password', form._hasWpAppPassword)}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Field label="Default category ID">{text('wpCategoryId', { type: 'number' })}</Field>
            <Field label="Default author ID">{text('wpAuthorId', { type: 'number' })}</Field>
          </div>
        </>
      )}

      {/* GHOST */}
      {form.kind === 'ghost' && (
        <>
          <Field label="Ghost Admin API URL *">{text('ghostAdminApiUrl', { placeholder: 'https://site.ghost.io' })}</Field>
          {secretField('ghostAdminApiKey', 'Admin API key (id:secret)', form._hasGhostKey)}
        </>
      )}

      {/* WEBHOOK */}
      {form.kind === 'webhook' && (
        <>
          <Field label="Webhook URL *">{text('webhookUrl', { placeholder: 'https://hooks.example.com/blog' })}</Field>
          {secretField('webhookSecret', 'HMAC secret', form._hasWebhookSecret)}
          <div style={{ fontSize: 11, color: '#888', marginTop: -4, marginBottom: 8 }}>
            When set, the request body is signed and sent in <code>X-Prigmar-Signature: sha256=&lt;hex&gt;</code>.
          </div>
        </>
      )}

      {/* CUSTOM */}
      {form.kind === 'custom' && (
        <>
          <Field label="URL *">{text('customUrl', { placeholder: 'https://cms.internal/posts' })}</Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Field label="Method">
              <select className="form-control" value={form.customMethod || 'POST'} onChange={(e) => set('customMethod', e.target.value)} style={{ fontSize: 13 }}>
                <option value="POST">POST</option>
                <option value="PUT">PUT</option>
                <option value="PATCH">PATCH</option>
              </select>
            </Field>
            <Field label="Auth type">
              <select className="form-control" value={form.customAuthType || 'none'} onChange={(e) => set('customAuthType', e.target.value)} style={{ fontSize: 13 }}>
                <option value="none">None</option>
                <option value="bearer">Bearer token</option>
                <option value="basic">HTTP Basic (user:pass)</option>
                <option value="header">Custom header</option>
                <option value="email">Publish by email (connected inbox)</option>
              </select>
            </Field>
          </div>
          {form.customAuthType === 'header' && (
            <Field label="Auth header name">{text('customAuthHeaderName', { placeholder: 'X-API-Key' })}</Field>
          )}
          {['bearer', 'basic', 'header'].includes(form.customAuthType) && secretField('customAuthValue',
            form.customAuthType === 'bearer' ? 'Token' :
            form.customAuthType === 'basic'  ? 'user:password' : 'Header value',
            form._hasCustomAuth)}
          {form.customAuthType === 'email' && (
            <div style={{ fontSize: 11, color: '#888', marginTop: -4, marginBottom: 8 }}>
              The post is emailed to the URL above (a recipient address, e.g. a CMS "post via email" inbox), authenticated with the connected inbox you pick under <strong>Author identity</strong> below — no token needed.
            </div>
          )}
          <Field label="Body template (optional)">
            <textarea className="form-control" value={form.customBodyTemplate || ''} rows={6} onChange={(e) => set('customBodyTemplate', e.target.value)}
                      placeholder={'{"title":"{{title}}","html":"{{bodyHtml}}","slug":"{{slug}}"}'}
                      style={{ fontSize: 12, fontFamily: 'ui-monospace, monospace' }} />
          </Field>
          <div style={{ fontSize: 11, color: '#888', marginTop: -4, marginBottom: 8 }}>
            Available placeholders: <code>{'{{title}}'}</code> <code>{'{{slug}}'}</code> <code>{'{{bodyHtml}}'}</code> <code>{'{{bodyMarkdown}}'}</code> <code>{'{{excerpt}}'}</code> <code>{'{{metaTitle}}'}</code> <code>{'{{metaDescription}}'}</code> <code>{'{{primaryKeyword}}'}</code> <code>{'{{publishedAt}}'}</code> <code>{'{{brand}}'}</code> <code>{'{{tags}}'}</code>. Leave blank to send the default JSON payload.
          </div>
        </>
      )}

      {/* AUTHOR IDENTITY — post as a connected email account, or a manual override */}
      <div style={{ borderTop: '1px solid #eee', marginTop: 10, paddingTop: 10 }}>
        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Author identity <span style={{ fontWeight: 400, color: '#888' }}>(optional)</span></div>
        <Field label="Post as connected email account">
          <select className="form-control" value={form.emailAccount_id || ''}
                  onChange={(e) => { const v = e.target.value || null; set('emailAccount_id', v); set('smtpAuthUser', ''); set('smtpAuthPassword', ''); testEmailAuth(v); }}
                  style={{ fontSize: 13 }}>
            <option value="">— none (use manual author below) —</option>
            {emailAccounts.map((a) => (
              <option key={a._id} value={a._id}>{a.fromName ? `${a.fromName} <${a.fromAddress}>` : (a.name || a.fromAddress)}</option>
            ))}
          </select>
        </Field>

        {/* Live SMTP auth result for the selected account. */}
        {form.emailAccount_id && authTest.status === 'testing' && (
          <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Testing authentication…</div>
        )}
        {form.emailAccount_id && authTest.status === 'ok' && (
          <div style={{ fontSize: 12, color: '#1e6b3e', marginBottom: 6 }}>✓ Authenticated — this inbox can send.</div>
        )}
        {form.emailAccount_id && authTest.status === 'fail' && (
          <div style={{ background: '#fdecec', border: '1px solid #f5c6cb', borderRadius: 6, padding: 8, marginBottom: 8 }}>
            <div style={{ fontSize: 12, color: '#a71d2a', marginBottom: 6 }}>
              Authentication failed{authTest.error ? `: ${authTest.error}` : ''}. Enter working credentials for this inbox:
            </div>
            <Field label="SMTP email / username">{text('smtpAuthUser', { placeholder: form.emailAccount_id ? 'name@brand.com' : '' })}</Field>
            {secretField('smtpAuthPassword', 'SMTP password', form._hasSmtpAuthPassword)}
            <button type="button" className="acc-btn" style={{ fontSize: 12 }}
                    onClick={() => testEmailAuth(form.emailAccount_id, form.smtpAuthUser, form.smtpAuthPassword)}>
              {authTest.status === 'testing' ? 'Testing…' : 'Re-test credentials'}
            </button>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <Field label="Author name">{text('authorName', { placeholder: 'e.g. Support team' })}</Field>
          <Field label="Author email">{text('authorEmail', { placeholder: 'author@brand.com' })}</Field>
        </div>
        <div style={{ fontSize: 11, color: '#888', marginTop: -4, marginBottom: 4 }}>
          Link a connected inbox to attribute/authorise posts with its identity, or set a manual author name/email. Leave all blank to use the brand default.
        </div>
      </div>

      <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6, marginTop: 8 }}>
        <input type="checkbox" checked={form.enabled !== false} onChange={(e) => set('enabled', e.target.checked)} />
        Enabled (uncheck to pause without deleting)
      </label>

      <div style={{ marginTop: 14, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button type="button" className="acc-btn" onClick={onClose} disabled={busy}>Cancel</button>
        <button type="button" className="acc-btn acc-btn-primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : (isEdit ? 'Save changes' : 'Create target')}</button>
      </div>
    </Modal>
  );
}

export default function PublishTargets() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [editing, setEditing] = useState(null); // null | { _id?: ... }
  const [testing, setTesting] = useState(null); // _id of row currently being tested

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const res = await get('/api/seo/publish-targets');
      setRows((res && res.data) || []);
    } catch (e) {
      setErr((e && (e.message || (e.response && e.response.data && e.response.data.error))) || 'Load failed');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onTest = async (row) => {
    setTesting(row._id);
    try {
      const res = await post(`/api/seo/publish-targets/${row._id}/test`, {}, false);
      if (res && res.error) alert(`Test failed: ${res.error}`);
      else if (res && res.data) alert(`Test succeeded.${res.data.externalUrl ? '\n\nURL: ' + res.data.externalUrl : ''}`);
      load();
    } catch (e) {
      alert(`Test failed: ${(e && e.message) || 'error'}`);
    } finally { setTesting(null); }
  };

  const onDelete = async (row) => {
    if (!window.confirm(`Delete publish target "${row.name}"?`)) return;
    try {
      await remove(`/api/seo/publish-targets/${row._id}`, {});
      load();
    } catch (e) {
      alert(`Delete failed: ${(e && e.message) || 'error'}`);
    }
  };

  return (
    <div style={{ padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div>
          <h3 style={{ margin: 0 }}>Publish Targets</h3>
          <div style={{ fontSize: 12, color: '#666' }}>Configure where published content goes — FAQ answers and articles (by category), to the brand's on-site learning page, WordPress, Ghost, a webhook, or any custom endpoint.</div>
        </div>
        <button className="acc-btn acc-btn-primary" onClick={() => setEditing({})}>+ New target</button>
      </div>

      {err && <div className="alert alert-warning" style={{ fontSize: 13 }}>{err}</div>}
      {loading ? (
        <div style={{ padding: 20, color: '#888' }}>Loading…</div>
      ) : rows.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', background: '#fafbfc', borderRadius: 8 }}>
          <div style={{ fontSize: 14, color: '#525252', marginBottom: 6 }}>No publish targets yet.</div>
          <div style={{ fontSize: 12, color: '#888', marginBottom: 14 }}>Add a destination so generated articles can flow into your blog automatically.</div>
          <button className="acc-btn acc-btn-primary" onClick={() => setEditing({})}>+ Configure your first target</button>
        </div>
      ) : (
        <table className="table table-hover" style={{ fontSize: 13 }}>
          <thead><tr>
            <th>Name</th><th>Kind</th><th>Publishes</th><th>Default status</th><th>Stats</th><th>Last error</th><th></th>
          </tr></thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row._id} style={{ opacity: row.enabled ? 1 : 0.55 }}>
                <td><strong>{row.name}</strong>{!row.enabled && <span style={{ marginLeft: 8, fontSize: 11, color: '#888' }}>(paused)</span>}</td>
                <td>{kindLabel(row.kind)}</td>
                <td>{(row.contentType || 'article') === 'faq' ? 'FAQ' : `Articles${row.category ? ` · ${row.category}` : ''}`}</td>
                <td>{row.defaultStatus || 'draft'}</td>
                <td>{statusChip(row)}</td>
                <td style={{ color: '#a71d2a', fontSize: 12 }}>{row.lastError ? row.lastError.slice(0, 80) : '—'}</td>
                <td style={{ textAlign: 'right' }}>
                  <button className="acc-btn" onClick={() => onTest(row)} disabled={testing === row._id} style={{ fontSize: 12, marginRight: 6 }}>{testing === row._id ? 'Testing…' : 'Test'}</button>
                  <button className="acc-btn" onClick={() => setEditing(row)} style={{ fontSize: 12, marginRight: 6 }}>Edit</button>
                  <button className="acc-btn" onClick={() => onDelete(row)} style={{ fontSize: 12, color: '#a71d2a' }}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editing && (
        <TargetForm
          target={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}
